// ABOUTME: Refreshes Kanban order, totals, and applicant cards from the API.
// ABOUTME: Protects persisted stage changes from stale Elasticsearch reads.
import { useJobContextSafe } from "../useJobContext";
import { useQueryClient } from "react-query";
import { getStateFromDB, getTotalStageFromDb, handleOrderData } from "./helper";
import _ from "lodash";
import { apiDirectCall } from "customHooks/useApi";
import useShowMessage from "customHooks/useShowMessage/useShowMessage";
import { useAuthState } from "_ReactContext/AuthContext";
import {
    ExpectedApplicantStage,
    getCanonicalApplicantStage,
    normalizeApplicantId,
    registerPendingApplicantStage
} from "./applicantStageReadConsistency";
import { startKanbanRefresh } from "./kanbanRefreshGeneration";
import { refreshKanbanAfterStage } from "./refreshKanbanAfterStage";

const useRefreshKanban = () => {
    const contextSafe = useJobContextSafe();
    const queryClient = useQueryClient();
    const showMessage = useShowMessage();
    const { company } = useAuthState();

    const updateAllInfo = async (
        message?: string,
        expectedStage?: ExpectedApplicantStage
    ) => {
        if (!contextSafe) return false;
        const [data, dispatchData] = contextSafe;
        const isCurrent = startKanbanRefresh(data.kanbanRefreshGeneration);
        const pendingApplicantStages =
            data.kanbanPendingApplicantStages.current;
        const currentFilter = _.cloneDeep(data.fullFilter);
        currentFilter.otherGenericParams = {
            ...currentFilter.otherGenericParams,
            filterStageIds: []
        };

        let dataOrderDB: any;
        const readOrderData = async () => {
            dataOrderDB = await apiDirectCall(
                data.apiKanbanOrderDataAddress,
                "post",
                { ...data.orderDataFilter, updateOrderData: false }
            );
            return (
                dataOrderDB?.data?.orderPipelineData?.priority?.orderData || []
            );
        };
        const invalidate = () => {
            queryClient.invalidateQueries(data.apiKeyApplicant);
            queryClient.invalidateQueries(data.apiKeyOrderData);
        };
        if (
            expectedStage &&
            !registerPendingApplicantStage(
                pendingApplicantStages,
                expectedStage
            )
        ) {
            invalidate();
            return false;
        }
        const getExpectedStages = () => [...pendingApplicantStages.values()];
        const loadAndApply = async (orderData: any[]) => {
            if (!isCurrent()) return;
            const stagesTotal = getTotalStageFromDb(
                dataOrderDB.data,
                data.LocalStateData.allStages
            );
            const newOrderData = handleOrderData(orderData);

            const dataApplicant = await apiDirectCall(
                data.apiKanbanListAddress,
                "post",
                currentFilter
            );
            if (!isCurrent()) return;

            const newStateData = getStateFromDB({
                dbData: dataApplicant,
                stateData: data.LocalStateData.ApplicantsData,
                orderData: newOrderData,
                onlyUpdateStagesFromDB: false
            });

            dispatchData.dispatchGeneralPageInfo({
                payload: {
                    ApplicantsData: newStateData,
                    orderData: newOrderData,
                    stagesOnlyTotal: stagesTotal
                },
                type: "update"
            });
            pendingApplicantStages.clear();
        };
        const pendingStages = getExpectedStages();
        const refreshed =
            pendingStages.length > 0
                ? await refreshKanbanAfterStage({
                      pendingStages: {
                          get: getExpectedStages,
                          clear: () => {
                              pendingApplicantStages.clear();
                          },
                          replace: (previousStage, nextStage) => {
                              const applicantId = normalizeApplicantId(
                                  previousStage.applicantId
                              );
                              const currentStage =
                                  pendingApplicantStages.get(applicantId);
                              if (
                                  currentStage?.requestId ===
                                  previousStage.requestId
                              ) {
                                  pendingApplicantStages.set(
                                      applicantId,
                                      nextStage
                                  );
                              }
                          }
                      },
                      readOrderData,
                      readAuthoritativeStage: async (pendingStage) => {
                          const response = await apiDirectCall(
                              "/job/getapplicantjobstage",
                              "post",
                              {
                                  companyId: company.companyId,
                                  jobId: data.JobId,
                                  applicantId: normalizeApplicantId(
                                      pendingStage.applicantId
                                  ),
                                  clientRequestId: pendingStage.requestId
                              }
                          );
                          return getCanonicalApplicantStage(response?.data);
                      },
                      loadAndApply,
                      invalidate,
                      isCurrent
                  })
                : (await loadAndApply(await readOrderData()), true);
        if (!refreshed || !isCurrent()) return false;

        queryClient.invalidateQueries(data.apiKeyApplicant);
        queryClient.invalidateQueries(data.apiKeyOrderData);
        queryClient.invalidateQueries("/reports/jobfunnel");
        if (message) {
            showMessage({
                message: message,
                timeout: 1000,
                snackBarOrign: { horizontal: "right", vertical: "bottom" }
            });
        }
        return true;
    };

    return updateAllInfo;
};

export default useRefreshKanban;
